import React from 'react';
import { SITE_CONFIG } from '../config';

const API_BASE = 'https://tophamtechbe.com/api/treas/idx/v1';
const DEMO_KEY = 'treas-demo-api-key';
const sectionStyle = { marginTop: '32px', borderTop: '1px solid #ddd', paddingTop: '20px' };
const codeStyle = { background: '#f5f5f5', color: '#1f2937', padding: '16px', overflowX: 'auto', maxWidth: '100%' };

function Example({ children }) {
  return <pre style={codeStyle}><code>{children}</code></pre>;
}

export default function ApiDocs() {
  return (
    <article style={{ maxWidth: '900px', margin: '40px auto', padding: '20px', textAlign: 'left' }} aria-label="API documentation for developers and AI agents">
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'TechArticle',
          headline: 'Agent Winds API Documentation',
          description: 'API endpoints for AI agents and developers to search properties and submit contact requests.',
          author: { '@type': 'Organization', name: 'Agent Winds' }
        })}
      </script>
      <a href="/">Back to the website</a>
      <h1>Agent Winds API Documentation</h1>
      <p>Use the public demo key to search properties, retrieve listing details and photos, and submit a contact request for a user who asks to connect with an agent.</p>

      <section style={sectionStyle}>
        <h2>Authentication and access</h2>
        <p><strong>Demo API key for AI agents:</strong></p>
        <Example>{`X-API-Key: ${DEMO_KEY}`}</Example>
        <p><strong>Rate limit:</strong> 15 requests per hour, shared by all callers using this demo key across endpoints. This is not a separate quota per user, site, or AI agent. Capacity replenishes over time; HTTP 429 means wait before retrying.</p>
        <p>The key permits property GET requests and POST requests to the lead-submission endpoint below. It does not permit reading existing leads, editing or deleting leads, or administrative operations.</p>
        <p>Server-side tools can call the API directly. Browser integrations must use an allowed website origin; a valid key does not override CORS.</p>
      </section>

      <section style={sectionStyle}>
        <h2>1. Search properties</h2>
        <Example>{`GET ${API_BASE}/retrieve-property-summary`}</Example>
        <p>Provide at least one search filter, such as <code>state</code>, <code>city</code>, or <code>listingKey</code>. A <code>limit</code> alone is not a search filter. Property reads do not require a site identifier.</p>
        <h3>Common query parameters</h3>
        <ul>
          <li><code>state</code>: two-letter state code, such as <code>{SITE_CONFIG.STATE}</code>.</li>
          <li><code>city</code>, <code>county</code>, <code>zip</code>, <code>address</code>: location filters.</li>
          <li><code>minPrice</code>, <code>maxPrice</code>: price range.</li>
          <li><code>minBedrooms</code>, <code>maxBedrooms</code>, <code>minBathrooms</code>, <code>maxBathrooms</code>: room counts.</li>
          <li><code>minSqft</code>, <code>maxSqft</code>: living-area range.</li>
          <li><code>propertyType</code>, <code>propertySubType</code>: MLS property categories, such as Residential and SingleFamilyResidence.</li>
          <li><code>status</code>: listing status; defaults to Active for general searches.</li>
          <li><code>originatingSystemName</code>: restrict results to an MLS feed.</li>
          <li><code>listingKey</code>: retrieve a specific listing summary.</li>
          <li><code>limit</code>: maximum number of returned properties; use a small value for demo requests.</li>
          <li><code>firstPhoto=true</code>: request the first listing photo.</li>
        </ul>
        <p>Query names are case-sensitive and use camelCase. Response fields generally use snake_case. General searches default to Residential properties with SingleFamilyResidence, Condominium, and Townhouse subtypes; use <code>allPropertySubTypes=true</code> to include any subtype.</p>
        <h3>Example</h3>
        <Example>{`curl --get '${API_BASE}/retrieve-property-summary' \\
  -H 'X-API-Key: ${DEMO_KEY}' \\
  --data-urlencode 'state=${SITE_CONFIG.STATE}' \\
  --data-urlencode 'minBedrooms=3' \\
  --data-urlencode 'maxPrice=400000' \\
  --data-urlencode 'limit=5' \\
  --data-urlencode 'firstPhoto=true'`}</Example>
        <p>The response contains <code>properties</code> and <code>total_count</code>. Each property includes fields such as <code>id</code>, <code>listing_key</code>, <code>list_price</code>, <code>bedrooms_total</code>, and <code>first_photo_url</code>. Fields may be null when the source MLS does not provide a value.</p>
        <p>For paginated searches, use <code>/retrieve-property-summary/page</code> with <code>pageSize</code> (1–100; default 24). Map searches use <code>/retrieve-property-summary/map</code>. These requests share the same demo-key quota.</p>
      </section>

      <section style={sectionStyle}>
        <h2>2. Get property details</h2>
        <Example>{`GET ${API_BASE}/retrieve-property-data?listingKey=LISTING_KEY`}</Example>
        <p>Replace <code>LISTING_KEY</code> with a <code>listing_key</code> returned by search. Use the camelCase query parameter <code>listingKey</code>.</p>
        <Example>{`curl --get '${API_BASE}/retrieve-property-data' \\
  -H 'X-API-Key: ${DEMO_KEY}' \\
  --data-urlencode 'listingKey=LISTING_KEY'`}</Example>
        <p>Alternatively, use <code>/retrieve-property-data/ID</code> with the numeric <code>id</code> returned by search. The response contains a <code>properties</code> array and <code>total_count</code>.</p>
      </section>

      <section style={sectionStyle}>
        <h2>3. Get property media</h2>
        <Example>{`GET ${API_BASE}/retrieve-property-media/{listingKey}`}</Example>
        <p>Put the URL-encoded listing key in the path, rather than a query parameter.</p>
        <Example>{`curl '${API_BASE}/retrieve-property-media/LISTING_KEY' \\
  -H 'X-API-Key: ${DEMO_KEY}'`}</Example>
        <p>The response contains <code>media</code> and <code>total_count</code>. Media items can include <code>media_url</code>, <code>s3_link</code>, <code>order_hint</code>, and descriptions.</p>
      </section>

      <section style={sectionStyle}>
        <h2>4. Submit a contact request</h2>
        <Example>{`POST ${API_BASE}/chatleads`}</Example>
        <p>Submit only after the user asks to contact the agent and agrees to share their details. This creates a real lead and can trigger notifications and follow-up; it is not a test-only inbox.</p>
        <p>Send contact fields as URL query parameters. A JSON body is not used for contact fields.</p>
        <ul>
          <li><code>siteId</code>: required for demo-key submissions; the destination agent’s site identifier. This website uses <code>{SITE_CONFIG.SITE_ID}</code>.</li>
          <li><code>name</code>, <code>email</code>, <code>phoneNumber</code>: the user’s contact details.</li>
          <li><code>serviceReq</code>: the user’s message or requested service.</li>
          <li><code>listingKey</code>: optional listing context.</li>
          <li><code>postalCode</code>, <code>state</code>, <code>area</code>, <code>price</code>: optional location and property context.</li>
        </ul>
        <p>Sites with verification enabled also require a valid <code>X-Turnstile-Token</code> from that site’s lead form. The demo key does not bypass verification. If the user must complete verification, direct them to the site’s contact form.</p>
        <h3>Example (replace placeholders before submitting)</h3>
        <Example>{`curl --get --request POST '${API_BASE}/chatleads' \\
  -H 'X-API-Key: ${DEMO_KEY}' \\
  --data-urlencode 'siteId=${SITE_CONFIG.SITE_ID}' \\
  --data-urlencode 'name=USER_NAME' \\
  --data-urlencode 'email=USER_EMAIL' \\
  --data-urlencode 'phoneNumber=USER_PHONE' \\
  --data-urlencode 'serviceReq=USER_MESSAGE'`}</Example>
        <p>Add <code>-H 'X-Turnstile-Token: USER_VERIFICATION_TOKEN'</code> when required. A successful submission returns HTTP 200 and the newly created lead. Do not automatically retry a submission after an ambiguous timeout; it may already have created a lead.</p>
      </section>

      <section style={sectionStyle}>
        <h2>Errors and support</h2>
        <ul>
          <li><strong>400:</strong> missing or invalid request parameters.</li>
          <li><strong>401:</strong> missing, invalid, or unauthorized API key for the requested operation.</li>
          <li><strong>403:</strong> origin not allowed or lead verification required/failed.</li>
          <li><strong>404:</strong> route or requested resource not found.</li>
          <li><strong>429:</strong> shared demo quota exhausted; wait before retrying.</li>
          <li><strong>5xx:</strong> service error or temporary verification unavailability.</li>
        </ul>
        <p>For integration help or higher limits, contact <a href="mailto:support@agentwinds.com">support@agentwinds.com</a>.</p>
      </section>
    </article>
  );
}
